import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { springs } from "@/components/ui/motion";
import type { WizardStep } from "./types";

const STEPS: { key: WizardStep; label: string }[] = [
  { key: "details", label: "Details" },
  { key: "generating", label: "Generating" },
  { key: "review", label: "Review" },
];

const STEP_INDEX: Record<WizardStep, number> = {
  details: 0,
  generating: 1,
  review: 2,
};

interface WizardStepIndicatorProps {
  currentStep: WizardStep;
}

export function WizardStepIndicator({ currentStep }: WizardStepIndicatorProps) {
  const currentIndex = STEP_INDEX[currentStep];
  const progressPercent =
    currentIndex === 0 ? 0 : currentIndex === 1 ? 50 : 100;

  return (
    <div className="pt-2 pb-1">
      <div className="flex items-center justify-between">
        {STEPS.map((step, index) => {
          const isCompleted = index < currentIndex;
          const isActive = index === currentIndex;

          return (
            <div key={step.key} className="flex items-center gap-2">
              <div
                className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-semibold transition-colors ${
                  isCompleted
                    ? "bg-accent text-white"
                    : isActive
                      ? "border-2 border-accent text-accent"
                      : "border-2 border-border text-muted"
                }`}
              >
                {isCompleted ? (
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={springs.bouncy}
                  >
                    <Check className="h-3.5 w-3.5" />
                  </motion.div>
                ) : (
                  index + 1
                )}
              </div>

              <span
                className={`text-[12px] font-medium ${
                  isActive
                    ? "text-accent"
                    : isCompleted
                      ? "text-primary"
                      : "text-muted"
                }`}
              >
                {step.label}
              </span>
            </div>
          );
        })}
      </div>

      <div className="mt-3 h-[3px] rounded-full bg-border/50">
        <motion.div
          className="h-full rounded-full bg-accent"
          initial={{ width: "0%" }}
          animate={{ width: `${progressPercent}%` }}
          transition={springs.smooth}
        />
      </div>
    </div>
  );
}
